import { PropsWithChildren, useEffect, useRef } from "react";
import ModalHeader, { type ModalHeaderConfig } from "@/components/Dialog/ModalHeader";
import DrawerModal from "./DrawerModal";
import ModalContext from "@/hooks/useModalContext";

type OpenSidebarProps = {
  header: ModalHeaderConfig;
  actions?: React.ReactNode;
  fixedContent?: React.ReactNode;
  closeSidebar: () => void;
  disableFocusManagement?: boolean;
};

const zIndex = 1000;
const OpenSidebar = ({
  children,
  closeSidebar,
  header,
  actions,
  fixedContent,
  disableFocusManagement = false
}: PropsWithChildren<OpenSidebarProps>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disableFocusManagement) return;

    let attempt = 0;
    const maxAttempts = 6;

    const tryFocus = () => {
      const root = containerRef.current;
      if (!root) return;

      const input = root.querySelector("md-outlined-text-field, md-filled-text-field") as HTMLElement | null;
      if (input) {
        input.focus();
        return;
      }

      if (++attempt < maxAttempts) {
        timeoutId = setTimeout(tryFocus, 100);
      }
    };

    let timeoutId = setTimeout(tryFocus, 100);

    return () => clearTimeout(timeoutId);
  }, [disableFocusManagement]);

  return (
    <div
      ref={containerRef}
      className="flex flex-col pt-8 absolute inset-0 bg-sys-brand-surface-container-low overflow-hidden"
    >
      <ModalHeader
        title={header.title}
        onBack={header.onBack}
        action={header.action}
        onClose={closeSidebar}
        disableClose={header.disableClose}
        className="self-stretch pl-6 pr-2 sm:pl-10 sm:pr-6"
        titleClassName="truncate pr-4 text-headline-medium-bold font-serif"
        backButtonClassName="!my-1 -ml-2.5"
        closeButtonClassName="!my-1"
      />
      <div className="w-full px-6 sm:px-10 border-b grow overflow-y-auto pb-6">
        <ModalContext.Provider value={true}>{children}</ModalContext.Provider>
      </div>
      {(actions || fixedContent) && (
        <div className="w-full flex flex-col flex-shrink-0 gap-2 px-6 sm:px-10 py-4">
          {fixedContent && <div className="w-full">{fixedContent}</div>}
          {actions && (
            <div className="flex flex-row-reverse flex-wrap-reverse justify-end items-start gap-2 w-full">
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface SidebarProps {
  isSidebarOpen: boolean;
  setSidebarOpen: (isOpen: boolean) => void;
  title?: React.ReactNode;
  header?: ModalHeaderConfig;
  actions?: React.ReactNode;
  fixedContent?: React.ReactNode;
  children: React.ReactNode;
  goBack?: () => void;
  disableClose?: boolean;
  disableFocusManagement?: boolean;
  dismissOnOutsideClick?: boolean;
  persistentTabLabel?: string;
  persistentTabStatus?: "active" | "inactive";
}

export default function SideSheetModal({
  isSidebarOpen,
  setSidebarOpen,
  title,
  header,
  actions,
  fixedContent,
  children,
  goBack,
  disableClose,
  disableFocusManagement,
  dismissOnOutsideClick,
  persistentTabLabel,
  persistentTabStatus
}: SidebarProps) {
  const resolvedHeader: ModalHeaderConfig = {
    title: header?.title ?? title ?? "",
    onBack: header?.onBack ?? goBack,
    action: header?.action,
    disableClose: header?.disableClose ?? disableClose
  };

  return (
    <DrawerModal
      zIndex={zIndex}
      isOpen={isSidebarOpen}
      onChange={setSidebarOpen}
      data-testid="drawer"
      disableClose={resolvedHeader.disableClose}
      dismissOnOutsideClick={dismissOnOutsideClick}
      persistentTabLabel={persistentTabLabel}
      persistentTabStatus={persistentTabStatus}
    >
      {isSidebarOpen ? (
        <OpenSidebar
          header={resolvedHeader}
          closeSidebar={() => setSidebarOpen(false)}
          actions={actions}
          fixedContent={fixedContent}
          disableFocusManagement={disableFocusManagement}
        >
          {children}
        </OpenSidebar>
      ) : null}
    </DrawerModal>
  );
}
